import React from "react"

import { AlertTriangle } from "lucide-react"
import { Marker } from "react-map-gl/mapbox"

import { LocationIdentity, MapCard } from "@/components/collections/monitoring/MapOverlayCard"
import { cn } from "@/lib/utils"
import { Stop } from "@/types/collections"

const getStopMarkerColor = (status: string) => {
  if (status === "completed") return "#6B7280"
  if (status === "skipped") return "#F97316"
  if (status === "processing") return "#3B82F6"
  return "#9CA3AF"
}

interface StopMarkerProps {
  stop: Stop
  index: number
  routeStops: Stop[]
  overlappingStops: Stop[]
  isOverlapping: boolean
  isHovered: boolean
  onHoverChange: (stopId: string | null) => void
}

const StopMarker: React.FC<StopMarkerProps> = ({
  stop,
  index,
  routeStops,
  overlappingStops,
  isOverlapping,
  isHovered,
  onHoverChange,
}) => {
  // Every stop number at this spot, this one included, so overlapping stops read "2 5".
  const allStopNumbers = (() => {
    if (!isOverlapping) return [index + 1]

    const numbers = [index + 1]
    overlappingStops.forEach((overlappingStop) => {
      const overlappingIndex = routeStops.findIndex((s) => s.id === overlappingStop.id)
      if (overlappingIndex !== -1) {
        numbers.push(overlappingIndex + 1)
      }
    })
    return numbers.sort((a, b) => a - b)
  })()

  const hasActual = stop.actualArrival !== undefined
  const hasETA = stop.estimatedArrival !== undefined
  let calculatedDelayMinutes = stop.delayMinutes
  if (!hasActual && hasETA && stop.estimatedArrival) {
    const delayMs = stop.estimatedArrival.getTime() - stop.plannedArrival.getTime()
    calculatedDelayMinutes = Math.round(delayMs / (1000 * 60))
  }

  const isBehind =
    calculatedDelayMinutes !== undefined &&
    calculatedDelayMinutes !== null &&
    calculatedDelayMinutes >= 5

  return (
    <Marker
      longitude={stop.lng}
      latitude={stop.lat}
      anchor="center"
      style={{ zIndex: isHovered ? 1000 : 0 }}
    >
      <div
        className="group relative cursor-pointer"
        onMouseEnter={() => onHoverChange(stop.id)}
        onMouseLeave={() => onHoverChange(null)}
      >
        <div className="absolute -top-5 left-1/2 flex -translate-x-1/2 items-center gap-0.5">
          {allStopNumbers.map((num) => (
            <div
              key={num}
              className="rounded bg-white px-1 text-[10px] font-bold text-palette-jungleGreen700 shadow-sm"
            >
              {num}
            </div>
          ))}
        </div>
        {isBehind && !hasActual && (
          <div className="absolute -right-1 -top-1 z-10 h-3 w-3 rounded-full border-2 border-white bg-red-500 shadow-md" />
        )}
        <div
          className={cn(
            "h-5 w-5 rounded-full border-2 shadow-md transition-transform group-hover:scale-125",
            isOverlapping ? "border-4 border-palette-jungleGreen500" : "border-white"
          )}
          style={{ backgroundColor: getStopMarkerColor(stop.status) }}
        />
        <div className="absolute bottom-full left-1/2 z-[70] mb-2 hidden -translate-x-1/2 group-hover:block">
          <MapCard>
            <div className="p-2.5">
              <LocationIdentity name={stop.name} address={stop.address} />
              {stop.status === "skipped" ? (
                <div className="mt-1 text-[10px] font-medium text-orange-600">Skipped</div>
              ) : (
                <div className="mt-1 text-[10px] text-palette-jungleGreen500">
                  ETA:{" "}
                  {stop.plannedArrival.toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              )}
            </div>

            {isOverlapping && overlappingStops.length > 0 && (
              <div className="border-t border-palette-jungleGreen100 px-2.5 pb-2.5 pt-2">
                <div className="mb-1 flex items-center gap-1 text-[10px] font-medium text-palette-jungleGreen600">
                  <AlertTriangle size={10} className="flex-shrink-0" />
                  Multiple stops here
                </div>
                <div className="max-h-[168px] space-y-1.5 overflow-y-auto">
                  {overlappingStops.map((overlappingStop) => {
                    const overlappingIndex = routeStops.findIndex(
                      (s) => s.id === overlappingStop.id
                    )
                    return (
                      <div
                        key={overlappingStop.id}
                        className="border-l-2 border-palette-jungleGreen200 pl-2"
                      >
                        <div className="text-[10px] font-semibold text-palette-jungleGreen400">
                          Stop {overlappingIndex + 1}
                        </div>
                        <LocationIdentity
                          name={overlappingStop.name}
                          address={overlappingStop.address}
                        />
                        {overlappingStop.status === "skipped" ? (
                          <div className="mt-0.5 text-[10px] font-medium text-orange-600">
                            Skipped
                          </div>
                        ) : (
                          <div className="mt-0.5 text-[10px] text-palette-jungleGreen500">
                            ETA:{" "}
                            {overlappingStop.plannedArrival.toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </MapCard>
        </div>
      </div>
    </Marker>
  )
}

export default StopMarker
